import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';
import { Button } from '../ui/button';
import {
  Send, CheckCircle, Clock, Users, Headphones, Mail, Building2, User,
  Phone, Briefcase, MessageSquare, Loader2
} from 'lucide-react';
import {
  contactHeroData, contactBenefitsData, businessTypesData,
  employeeRangesData, interestAreasData
} from '../../data/contactData';

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') || 'general';
  
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    empresa: '',
    cargo: '',
    tipoNegocio: '',
    tamanoEmpresa: '',
    areaInteres: '',
    mensaje: '',
    source: source
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    if (!formData.empresa.trim()) newErrors.empresa = 'La empresa es requerida';
    if (!formData.tipoNegocio) newErrors.tipoNegocio = 'Selecciona un tipo de negocio';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    // Payload con el shape exacto que espera el endpoint Supabase de captura de leads
    const payload = {
      org_id: '3b36b6c5-9e31-4924-8cbf-6992d031a88d',
      name: formData.nombre,
      email: formData.email,
      phone: formData.telefono || '',
      company: formData.empresa,
      job_title: formData.cargo || '',
      business_type: formData.tipoNegocio || '',
      company_size: formData.tamanoEmpresa || '',
      solution_interest: formData.areaInteres || '',
      message: formData.mensaje || '',
      source: 'webhook',
      source_detail: `asisant.com · ${formData.source || 'general'} · ${window.location.pathname}${window.location.search}`,
    };

    const endpoint = 'https://pusktiwoibptbduylipb.supabase.co/functions/v1/crm-capture-lead';
    const anonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InB1c2t0aXdvaWJwdGJkdXlsaXBiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI0OTY0ODIsImV4cCI6MjA4ODA3MjQ4Mn0.d55Qff_uBWr1wc9kWnR9s8wEA2XAg9t0u7u6Ol0Rta8';

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: anonKey,
          Authorization: `Bearer ${anonKey}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(`CRM endpoint respondió ${response.status}: ${errText}`);
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Hubo un error al enviar el formulario. Por favor, inténtalo de nuevo o escríbenos a info@asisant.com.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPageTitle = () => {
    switch (source) {
      case 'demo': return 'Solicitar Demo';
      case 'ventas': return 'Contactar Ventas';
      default: return 'Contacto';
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <section className="min-h-screen flex items-center justify-center bg-dark-ellipse">
          <div className="max-w-xl mx-auto px-4 text-center">
            <div className="bg-white rounded-3xl p-12 shadow-2xl">
              <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-green-100">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">¡Mensaje enviado!</h2>
              <p className="text-[#c3c3c5] mb-8">
                Gracias por tu interés en Asisant. Un especialista se pondrá en contacto contigo en las próximas 24 horas hábiles.
              </p>
              <Button
                onClick={() => window.location.href = '/'}
                className="text-white font-semibold px-8 py-6 rounded-full"
                style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
              >
                Volver al inicio
              </Button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO
        title="Contacto · Habla con un experto de asisant"
        description="Agenda una demo o consulta con nuestro equipo. Soluciones cloud, billing CSP, ITAM, MSP y telcos. Respuesta en menos de 24 horas."
        keywords="asisant contacto, demo, experto cloud, CSP, MSP"
        canonical="/contacto"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden bg-dark-ellipse">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-4 border border-white/20">
            {getPageTitle()}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {contactHeroData.title}
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            {contactHeroData.subtitle}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            
            {/* Benefits Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">¿Qué incluye?</h3>
                {contactBenefitsData.map((benefit, idx) => {
                  const icons = [Headphones, Users, Clock];
                  const IconComponent = icons[idx];
                  return (
                    <div key={benefit.id} className="flex gap-4 group">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">{benefit.title}</h4>
                        <p className="text-[#c3c3c5] text-sm">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}

                <div className="mt-8 p-6 bg-[#e3e4e6]/30 rounded-2xl">
                  <h4 className="font-bold text-gray-900 mb-3">¿Prefieres contacto directo?</h4>
                  <a href="mailto:info@asisant.com" className="flex items-center gap-2 text-[#1297fd] hover:text-[#40aaf8] transition-colors">
                    <Mail className="w-5 h-5" />
                    info@asisant.com
                  </a>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-[#e3e4e6] p-8 shadow-xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-8">Cuéntanos sobre ti</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Nombre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre completo *
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c3c3c5]" />
                      <input
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        placeholder="Tu nombre"
                        data-testid="contact-nombre"
                        className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:border-[#1297fd] transition-colors ${errors.nombre ? 'border-red-400' : 'border-[#e3e4e6]'}`}
                      />
                    </div>
                    {errors.nombre && <p className="text-red-500 text-sm mt-1">{errors.nombre}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email corporativo *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c3c3c5]" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="tu@empresa.com"
                        data-testid="contact-email"
                        className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:border-[#1297fd] transition-colors ${errors.email ? 'border-red-400' : 'border-[#e3e4e6]'}`}
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>

                  {/* Teléfono */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c3c3c5]" />
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        placeholder="+34 600 000 000"
                        data-testid="contact-telefono"
                        className="w-full pl-12 pr-4 py-3 border-2 border-[#e3e4e6] rounded-xl focus:outline-none focus:border-[#1297fd] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Empresa */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Empresa *
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c3c3c5]" />
                      <input
                        type="text"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleChange}
                        placeholder="Nombre de tu empresa"
                        data-testid="contact-empresa"
                        className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:border-[#1297fd] transition-colors ${errors.empresa ? 'border-red-400' : 'border-[#e3e4e6]'}`}
                      />
                    </div>
                    {errors.empresa && <p className="text-red-500 text-sm mt-1">{errors.empresa}</p>}
                  </div>

                  {/* Cargo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cargo / Rol
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c3c3c5]" />
                      <input
                        type="text"
                        name="cargo"
                        value={formData.cargo}
                        onChange={handleChange}
                        placeholder="Ej: Director Comercial"
                        data-testid="contact-cargo"
                        className="w-full pl-12 pr-4 py-3 border-2 border-[#e3e4e6] rounded-xl focus:outline-none focus:border-[#1297fd] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Tipo de Negocio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de negocio *
                    </label>
                    <select
                      name="tipoNegocio"
                      value={formData.tipoNegocio}
                      onChange={handleChange}
                      data-testid="contact-tipo-negocio"
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-[#1297fd] transition-colors appearance-none bg-white ${errors.tipoNegocio ? 'border-red-400' : 'border-[#e3e4e6]'}`}
                    >
                      <option value="">Seleccionar...</option>
                      {businessTypesData.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                    {errors.tipoNegocio && <p className="text-red-500 text-sm mt-1">{errors.tipoNegocio}</p>}
                  </div>

                  {/* Tamaño Empresa */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tamaño de empresa
                    </label>
                    <select
                      name="tamanoEmpresa"
                      value={formData.tamanoEmpresa}
                      onChange={handleChange}
                      data-testid="contact-tamano"
                      className="w-full px-4 py-3 border-2 border-[#e3e4e6] rounded-xl focus:outline-none focus:border-[#1297fd] transition-colors appearance-none bg-white"
                    >
                      <option value="">Seleccionar...</option>
                      {employeeRangesData.map(range => (
                        <option key={range.value} value={range.value}>{range.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Área de Interés */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ¿Qué solución te interesa?
                    </label>
                    <select
                      name="areaInteres"
                      value={formData.areaInteres}
                      onChange={handleChange}
                      data-testid="contact-interes"
                      className="w-full px-4 py-3 border-2 border-[#e3e4e6] rounded-xl focus:outline-none focus:border-[#1297fd] transition-colors appearance-none bg-white"
                    >
                      <option value="">Seleccionar...</option>
                      {interestAreasData.map(area => (
                        <option key={area.value} value={area.value}>{area.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Mensaje */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ¿Cómo podemos ayudarte?
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-[#c3c3c5]" />
                    <textarea
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleChange}
                      placeholder="Cuéntanos sobre tu proyecto, necesidades actuales o cualquier duda que tengas..."
                      rows={4}
                      data-testid="contact-mensaje"
                      className="w-full pl-12 pr-4 py-3 border-2 border-[#e3e4e6] rounded-xl focus:outline-none focus:border-[#1297fd] transition-colors resize-none"
                    />
                  </div>
                </div>

                {/* Privacy Notice */}
                <p className="text-xs text-[#c3c3c5] mt-6">
                  Al enviar este formulario, aceptas nuestra política de privacidad. Tus datos serán tratados de forma confidencial y nunca serán compartidos con terceros.
                </p>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit"
                  className="w-full mt-8 text-white font-semibold py-6 rounded-full text-lg transition-all hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 w-5 h-5" />
                      Enviar solicitud
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
